import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Briefcase,
  UserSearch,
  ClipboardCheck,
  UserCog,
  ArrowRight,
  ArrowUpRight,
  CheckCircle2,
  Sparkles,
} from "lucide-react";

import {
  TechLabel,
  StatusBadge,
  CornerMarkers,
  PageContainer,
  colors as DS,
} from "../../design-system";
import { createPageUrl } from "../../utils";

const products = [
  {
    code: "JOBS",
    icon: Briefcase,
    title: "WebIdeal Jobs",
    tagline: "AI-powered job portal for candidates",
    accent: DS.product.jobs,
    href: createPageUrl("ProductJobs"),
    features: [
      "Predictive fit scoring",
      "One-click smart apply",
      "Live alerts within seconds",
      "24/7 candidate chatbot",
    ],
  },
  {
    code: "RECRUIT",
    icon: UserSearch,
    title: "WebIdeal Recruit",
    tagline: "Recruitment CRM for staffing teams",
    accent: DS.product.recruit,
    href: createPageUrl("ProductRecruit"),
    features: [
      "Multi-stage pipelines",
      "AI sourcing across networks",
      "Targets, KPIs, leaderboards",
      "Shared client portal",
    ],
  },
  {
    code: "ATS",
    icon: ClipboardCheck,
    title: "WebIdeal ATS",
    tagline: "Applicant tracking, end to end",
    accent: DS.product.ats,
    href: createPageUrl("ProductATS"),
    features: [
      "Customisable workflows",
      "AI screening + scorecards",
      "Automated communications",
      "Funnel analytics",
    ],
  },
  {
    code: "HRMS",
    icon: UserCog,
    title: "WebIdeal HRMS",
    tagline: "People ops, end to end",
    accent: DS.product.hrms,
    href: createPageUrl("ProductHRMS"),
    features: [
      "Onboarding & e-boarding",
      "Time, leave, payroll",
      "Performance & reviews",
      "Multi-country compliance",
    ],
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.45, delay: i * 0.06, ease: [0.25, 0.1, 0.25, 1] },
  }),
};

export default function Products() {
  return (
    <div
      className="relative"
      style={{
        background: DS.paper,
        color: DS.forest,
        fontFamily: "'Plus Jakarta Sans', system-ui, -apple-system, sans-serif",
      }}
    >
      {/* HERO */}
      <section
        className="relative pt-16 pb-20 md:pt-24 md:pb-28 border-b ds-bg-spotlight"
        style={{ borderColor: DS.hairline }}
      >
        <PageContainer>
          <motion.div initial="hidden" animate="show" variants={fadeUp}>
            <motion.div variants={fadeUp} custom={0}>
              <StatusBadge>The Suite · 04 modules</StatusBadge>
            </motion.div>
            <motion.h1
              variants={fadeUp}
              custom={1}
              className="mt-5 text-5xl sm:text-6xl lg:text-[5.5rem] xl:text-[6.5rem] font-extrabold leading-[0.92] tracking-tight max-w-5xl"
              style={{ color: DS.forest }}
            >
              Four products.
              <br />
              <span style={{ color: `${DS.forest}80` }}>One ecosystem.</span>
            </motion.h1>
            <motion.p
              variants={fadeUp}
              custom={2}
              className="mt-6 text-lg md:text-xl leading-relaxed max-w-3xl"
              style={{ color: DS.grid }}
            >
              Every tool you need, sharing the same data, the same AI brain and the same beautiful interface.
              Pay for what you use, ship every team's workflow on a single record.
            </motion.p>
          </motion.div>
        </PageContainer>
      </section>

      {/* PRODUCT GRID */}
      <section className="py-20 md:py-24 border-b" style={{ borderColor: DS.hairline }}>
        <PageContainer>
          <div className="grid md:grid-cols-2 gap-px" style={{ background: DS.hairline }}>
            {products.map((p, i) => (
              <motion.div
                key={p.code}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.5, delay: i * 0.07 }}
              >
                <Link
                  to={p.href}
                  className="ds-spatial group relative h-full block p-8 md:p-10 overflow-hidden transition-colors duration-200 hover:bg-white"
                  style={{ background: DS.paper, borderLeft: `4px solid ${p.accent}` }}
                >
                  <span
                    aria-hidden="true"
                    className="pointer-events-none absolute -top-16 -right-16 w-48 h-48 opacity-[0.12] blur-3xl transition-opacity duration-500 group-hover:opacity-[0.24]"
                    style={{ background: p.accent }}
                  />

                  <div className="relative flex items-start justify-between mb-7">
                    <div
                      className="w-16 h-16 flex items-center justify-center transition-transform duration-300 group-hover:-translate-y-0.5"
                      style={{ background: DS.forest, color: DS.paper }}
                    >
                      <p.icon className="w-7 h-7" strokeWidth={1.6} />
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      <TechLabel style={{ color: p.accent }}>P-0{i + 1} · {p.code}</TechLabel>
                      <span
                        className="w-9 h-9 flex items-center justify-center border transition-colors duration-200 group-hover:border-[#1A3C2B] group-hover:bg-[#1A3C2B]"
                        style={{ borderColor: DS.hairline, color: DS.grid }}
                      >
                        <ArrowUpRight className="w-4 h-4 transition-colors duration-200 group-hover:text-[#F7F7F5]" />
                      </span>
                    </div>
                  </div>

                  <div className="relative">
                    <TechLabel style={{ color: `${DS.grid}99` }}>{p.tagline}</TechLabel>
                    <h3
                      className="mt-2 text-3xl md:text-4xl font-bold leading-tight tracking-tight"
                      style={{ color: DS.forest }}
                    >
                      {p.title}
                    </h3>
                  </div>

                  <ul className="mt-6 grid grid-cols-1 gap-2.5">
                    {p.features.map((feat) => (
                      <li key={feat} className="flex items-start gap-2.5 text-sm" style={{ color: DS.grid }}>
                        <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: p.accent }} strokeWidth={2} />
                        <span>{feat}</span>
                      </li>
                    ))}
                  </ul>

                  <div
                    className="relative mt-8 pt-4 border-t flex items-center justify-between"
                    style={{ borderColor: DS.hairline }}
                  >
                    <div className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5" style={{ background: p.accent }} aria-hidden="true" />
                      <TechLabel style={{ color: `${DS.grid}aa` }}>module · live</TechLabel>
                    </div>
                    <span className="inline-flex items-center gap-1.5 text-sm font-semibold" style={{ color: DS.forest }}>
                      Explore
                      <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                    </span>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </PageContainer>
      </section>

      {/* WHY UNIFIED */}
      <section className="py-20 md:py-24 border-b" style={{ borderColor: DS.hairline, background: DS.forest, color: DS.paper }}>
        <PageContainer>
          <div className="mb-10 max-w-2xl">
            <StatusBadge color={DS.mint}>05 · Why one ecosystem</StatusBadge>
            <h2 className="mt-4 text-4xl md:text-5xl font-bold leading-[1.05] tracking-tight">
              The suite advantage, briefly.
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-px" style={{ background: "rgba(158,255,191,0.22)" }}>
            {[
              { idx: "01", title: "Single source of truth", desc: "One candidate, one employee record across Jobs, Recruit, ATS and HRMS." },
              { idx: "02", title: "AI shared across modules", desc: "The same fit scoring runs on the candidate portal and your ATS pipeline." },
              { idx: "03", title: "One bill, one login",     desc: "Per-seat pricing across the suite no per-tool overhead." },
            ].map((b) => (
              <div key={b.idx} className="p-7 md:p-8" style={{ background: DS.forest }}>
                <TechLabel style={{ color: DS.mint }}>{b.idx}</TechLabel>
                <h3 className="mt-2 text-xl font-bold">{b.title}</h3>
                <p className="mt-2 text-sm leading-relaxed" style={{ color: "rgba(247,247,245,0.75)" }}>
                  {b.desc}
                </p>
              </div>
            ))}
          </div>
        </PageContainer>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-24">
        <PageContainer>
          <div
            className="relative border p-10 md:p-14 text-center"
            style={{ borderColor: DS.hairline, background: DS.paper }}
          >
            <CornerMarkers color={DS.forest} />
            <div className="flex justify-center mb-5">
              <StatusBadge>Get started</StatusBadge>
            </div>
            <h2 className="text-3xl md:text-4xl xl:text-5xl font-bold leading-[1.05] tracking-tight" style={{ color: DS.forest }}>
              Pick a module. Or take the suite.
            </h2>
            <p className="mt-4 text-base md:text-lg max-w-2xl mx-auto" style={{ color: DS.grid }}>
              Talk to us about your stack and we'll show you the fastest path to one unified record.
            </p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
              <Link to={createPageUrl("Contact")}>
                <button type="button" className="ds-skeu-btn-primary cursor-pointer h-12 px-7 text-sm font-semibold inline-flex items-center">
                  <Sparkles className="w-4 h-4 mr-2" />
                  Book a demo
                </button>
              </Link>
              <Link to={createPageUrl("Pricing")}>
                <button
                  type="button"
                  className="cursor-pointer h-12 px-6 text-sm font-semibold border bg-transparent inline-flex items-center group transition-colors duration-150"
                  style={{ borderColor: DS.forest, color: DS.forest }}
                >
                  See pricing
                  <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-0.5" />
                </button>
              </Link>
            </div>
          </div>
        </PageContainer>
      </section>
    </div>
  );
}
